import { motion } from 'framer-motion'
import { Scale } from 'lucide-react'
import { ScrollReveal } from '@/components/ui'

/**
 * TermsPage - Minimal terms of service
 *
 * Pure black. Pure white. Pure agreement.
 * Matches lukhas.id design language.
 */

export default function TermsPage() {
  return (
    <div className="pb-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <Scale className="w-8 h-8 text-white/30" strokeWidth={1} />
              <span className="text-xs text-white/30 uppercase tracking-widest">Legal</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-white/40 font-light">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>

          {/* Agreement Notice */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-16 p-8 border border-white/20"
          >
            <p className="text-white/70 font-light leading-relaxed">
              By accessing or using LUKHAS services, you agree to be bound by these Terms of Service.
              If you disagree with any part of these terms, you may not access our services.
            </p>
          </motion.section>

          {/* Sections */}
          <div className="space-y-16">
            {/* Definitions */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                1. Definitions
              </h2>
              <div className="space-y-4">
                {[
                  { term: 'Services', def: 'The LUKHAS platform, including all websites, APIs, and applications' },
                  { term: 'User', def: 'The individual or entity accessing or using our Services' },
                  { term: 'Content', def: 'Text, code, data, prompts, outputs, and any materials created through our Services' },
                  { term: '\u039BiD', def: 'Our authentication and identity management system' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="text-white/70 font-light text-sm w-24 flex-shrink-0">{item.term}</span>
                    <span className="text-white/40 font-light text-sm">{item.def}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Acceptable Use */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                2. Acceptable Use
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                You agree to use our Services responsibly and in compliance with all applicable laws. You may:
              </p>
              <div className="space-y-3">
                {[
                  'Use LUKHAS for personal, commercial, or research purposes',
                  'Create applications and products using our APIs',
                  'Share outputs generated by LUKHAS systems',
                  'Integrate LUKHAS into your workflows',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/40" />
                    <span className="text-white/60 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Prohibited Activities */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                3. Prohibited Activities
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                You agree NOT to:
              </p>
              <div className="space-y-3">
                {[
                  'Use Services for illegal activities or to violate any laws',
                  'Generate harmful, abusive, or discriminatory content',
                  'Reverse engineer, decompile, or extract our models',
                  'Bypass rate limits, authentication, or security measures',
                  'Create misleading AI-generated content without disclosure',
                  'Interfere with or disrupt our Services',
                  'Scrape or harvest data without permission',
                  'Share your account credentials',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/20" />
                    <span className="text-white/50 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* User Accounts */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                4. User Accounts
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                When you create an account authenticated via \u039BiD, you are responsible for:
              </p>
              <div className="space-y-3">
                {[
                  'Maintaining the confidentiality of your credentials',
                  'All activities that occur under your account',
                  'Notifying us immediately of any unauthorized use',
                  'Providing accurate and up-to-date information',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/40" />
                    <span className="text-white/60 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Intellectual Property */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                5. Intellectual Property
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-white/80 font-light mb-2">LUKHAS IP</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    All rights in the Services, including software, models, and branding, remain the exclusive
                    property of LUKHAS AI.
                  </p>
                </div>
                <div>
                  <h3 className="text-white/80 font-light mb-2">Your Content</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    You retain ownership of content you create. By using our Services, you grant LUKHAS a
                    license to use your content solely to provide and improve Services.
                  </p>
                </div>
                <div>
                  <h3 className="text-white/80 font-light mb-2">Generated Outputs</h3>
                  <p className="text-white/50 font-light leading-relaxed text-sm">
                    Subject to compliance with these Terms, you own the outputs generated by LUKHAS in
                    response to your inputs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Disclaimer */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                6. Disclaimer of Warranties
              </h2>
              <div className="p-6 border border-white/10">
                <p className="text-white/70 font-light text-sm uppercase tracking-wider mb-4">
                  Services are provided "as is" without warranties of any kind.
                </p>
                <p className="text-white/40 font-light text-sm leading-relaxed">
                  We do not warrant that Services will be uninterrupted, secure, or error-free, or that
                  results will be accurate or reliable. You use Services at your own risk.
                </p>
              </div>
            </ScrollReveal>

            {/* Limitation of Liability */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                7. Limitation of Liability
              </h2>
              <p className="text-white/50 font-light text-sm leading-relaxed">
                To the maximum extent permitted by law, LUKHAS AI shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages. Our total liability shall not
                exceed the greater of $100 USD or the amount you paid us in the 12 months preceding the claim.
              </p>
            </ScrollReveal>

            {/* Termination */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                8. Termination
              </h2>
              <p className="text-white/50 font-light text-sm leading-relaxed mb-4">
                We may suspend or terminate your access immediately for any breach of these Terms.
                You may terminate your account at any time. Upon termination:
              </p>
              <div className="space-y-3">
                {[
                  'Your right to use Services immediately ceases',
                  'We may delete your account and associated data',
                  'Provisions that should survive termination shall survive',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/40" />
                    <span className="text-white/60 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Governing Law */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                9. Governing Law
              </h2>
              <p className="text-white/50 font-light text-sm leading-relaxed">
                These Terms are governed by the laws of the State of Delaware, USA. Any disputes shall be
                resolved through binding arbitration in accordance with AAA rules. You waive any right
                to a jury trial or to participate in a class action lawsuit.
              </p>
            </ScrollReveal>

            {/* Contact */}
            <ScrollReveal direction="up" blur>
              <div className="text-center py-12 border-t border-white/5">
                <h2 className="text-2xl font-light text-white mb-4">Questions?</h2>
                <p className="text-white/40 font-light mb-8">
                  Contact our legal team for terms-related inquiries.
                </p>
                <a
                  href="mailto:legal@lukhas.id"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-sm font-medium tracking-wide hover:bg-white/90 transition-colors duration-300"
                >
                  legal@lukhas.id
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
    </div>
  )
}
